import { useState, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PageLayout from '@/components/PageLayout'
import Header from '@/components/Header'
import Back from '@/components/Back'
import RefreshList from '@/components/RefreshList'
import Ripple from '@/components/Ripple'
import SkeletonBox from '@/components/SkeletonBox'
import EmptyState from '@/components/EmptyState'
import { APIWeb } from '@/api'
import dayjs from 'dayjs'

// Tab 类型
type TabType = 'all' | 'message' | 'notification'

// 消息项类型
interface MessageItem {
  id?: number
  title?: string
  message?: string
  date?: string
  type?: string
  isRead?: boolean
}

export default function Message() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Tab 状态
  const [activeTab, setActiveTab] = useState<TabType>('all')

  // 列表状态
  const [data, setData] = useState<MessageItem[]>([])
  const [loading, setLoading] = useState(false)
  const [hasMore, setHasMore] = useState(false)

  // Tab 配置
  const tabs = useMemo(() => [
    { key: 'all' as TabType, label: t('全部') },
    { key: 'message' as TabType, label: t('消息') },
    { key: 'notification' as TabType, label: t('通知') },
  ], [t])

  // 获取/刷新消息列表
  const onRefresh = useCallback(async () => {
    setLoading(true)
    try {
      const response = await APIWeb.getMessageListV2()
      
      if (response && response.length > 0) {
        // 将数组数据映射为 MessageItem
        const messageList: MessageItem[] = response.map((item: any, index: number) => ({
          id: index + 1,
          title: item?.title,
          message: item?.message,
          date: item?.date,
          type: item?.type,
          isRead: false
        }))
        setData(messageList)
      } else {
        setData([])
      }
      setHasMore(false)
    } catch (error) {
      console.error('获取消息列表失败:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  // 简化版本：所有 tab 显示相同数据
  const filteredData = data

  // 点击消息进入详情
  const handleMessageClick = useCallback((item: MessageItem) => {
    navigate('/message/detail', {
      state: { message: item }
    })
  }, [navigate])

  // 渲染消息项
  const renderMessageItem = useCallback((item: MessageItem, _index: number) => {
    const isNotification = item.type === 'notification'
    
    return (
      <Ripple className="rounded-lg">
        <div
          className="flex items-start py-4 px-5 border-b border-[#F2F3F5]"
          onClick={() => handleMessageClick(item)}
        >
          {/* 左侧图标 */}
          <div className="w-8 h-8 flex items-center justify-center mr-3 flex-shrink-0">
            {isNotification ? (
              <img src="/icon/message-notice.svg" alt="notice" className="w-6 h-6" />
            ) : (
              <img src="/icon/message-msg.svg" alt="message" className="w-6 h-6" />
            )}
          </div>

          {/* 内容 */}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-[#1D2129] mb-1 truncate">
              {item.title}
            </div>
            <div className="text-xs text-[#86909C] mb-1.5 line-clamp-2">
              {item.message}
            </div>
            <div className="text-xs text-[#C9CDD4]">
              {item.date ? dayjs(item.date).format('YYYY-MM-DD HH:mm:ss') : ''}
            </div>
          </div>

          {/* 未读红点 */}
          {!item.isRead && (
            <div className="w-2 h-2 bg-red-500 rounded-full ml-2 mt-1 flex-shrink-0" />
          )}
        </div>
      </Ripple>
    )
  }, [handleMessageClick])

  // 骨架屏
  const renderSkeleton = useCallback(() => (
    <div className="flex items-start py-4 px-5">
      <SkeletonBox className="w-8 h-8 rounded-lg mr-3 flex-shrink-0" />
      <div className="flex-1">
        <SkeletonBox className="w-32 h-4 rounded mb-2" />
        <SkeletonBox className="w-full h-3 rounded mb-1.5" />
        <SkeletonBox className="w-24 h-3 rounded" />
      </div>
    </div>
  ), [])


  return (
    <PageLayout>
      <Header
        leftChildren={<Back />}
        centerChildren={<span className="text-base font-medium">{t('通知')}</span>}
      />

      {/* Tabs */}
      <div className="px-5 pt-4 pb-2 border-b border-[#F2F3F5]">
        <div className="flex space-x-3">
          {tabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? 'bg-[#556AFF] text-white'
                  : 'bg-[#F7F8FA] text-[#4E5969]'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* 消息列表 */}
      <div className="bg-white flex flex-col flex-1">
        <RefreshList
          data={filteredData}
          renderItem={renderMessageItem}
          keyExtractor={(item, index) => `${item.id || index}`}
          loading={loading}
          hasMore={hasMore}
          onRefresh={onRefresh}
          onLoadMore={() => Promise.resolve()}
          renderSkeleton={renderSkeleton}
          skeletonCount={8}
          emptyComponent={
            <EmptyState
                text={t('暂无通知')}
                icon={
                <img src="/icon/empty-notification.svg" alt="empty" />
                }
            />
          }
          finishedText=""
          className="flex-1"
        />
      </div>
    </PageLayout>
  )
}

